import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getHotelList, type PmsHotelCacheItem } from "../service/hotelApi";
import { fetchAccessibleStoreIdsFromMsOrg } from "../service/msOrgAccessibleStores";
import { pushHisDebugLog } from "../utils/hisDebugLog";

export interface HotelOptionRow {
  value: number;
  label: string;
}

/**
 * 门店下拉：msOrg staff_stores ∩ 中台 pms_hotel_cache。
 * 仅 mount 时拉取一次 + 手动 refetch；不在 visibility/focus 上自动重拉（企微 WebView 易误触发导致一直 loading）。
 */
export function useMsOrgAccessibleHotelOptions() {
  const [allowedIds, setAllowedIds] = useState<number[] | null>(null);
  const [hotels, setHotels] = useState<PmsHotelCacheItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadSeq = useRef(0);

  const reload = useCallback(async (reason: string) => {
    const seq = ++loadSeq.current;
    pushHisDebugLog(`stores:reload start (${reason})`);
    setLoading(true);
    setLoadError(null);
    const t0 = typeof performance !== "undefined" ? performance.now() : 0;
    try {
      const ids = await fetchAccessibleStoreIdsFromMsOrg();
      if (seq !== loadSeq.current) return;
      setAllowedIds(ids);

      const hotelResp = await getHotelList(
        { pageNum: 1, pageSize: 2000 },
        { bypassHttpCache: true }
      );
      if (seq !== loadSeq.current) return;
      const list =
        hotelResp.code === 1 && Array.isArray(hotelResp.data?.list)
          ? hotelResp.data.list
          : [];
      setHotels(list);
      const ms =
        typeof performance !== "undefined" ? Math.round(performance.now() - t0) : 0;
      pushHisDebugLog(
        `stores:reload done reason=${reason} allowed=${ids.length} hotels=${list.length} ${ms}ms`
      );
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setAllowedIds([]);
      setHotels([]);
      const msg = e instanceof Error ? e.message : "加载门店失败";
      setLoadError(msg);
      pushHisDebugLog(`stores:reload FAIL (${reason}) ${msg}`);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload("mount");
  }, [reload]);

  const hotelOptions: HotelOptionRow[] = useMemo(() => {
    if (allowedIds == null) return [];
    const allow = new Set(allowedIds);
    const list = Array.isArray(hotels) ? hotels : [];
    return list
      .filter((h) => {
        if (h?.id == null || !(h.name || h.fullName)) return false;
        const id = typeof h.id === "number" ? h.id : Number(h.id);
        if (!Number.isFinite(id)) return false;
        return allow.has(id);
      })
      .map((h) => ({
        value: typeof h.id === "number" ? h.id : Number(h.id),
        label: h.name || h.fullName || `门店 ${h.id}`,
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), "zh-CN"));
  }, [allowedIds, hotels]);

  return {
    hotelOptions,
    loading,
    loadError,
    allowedIds,
    refetchAccessibleStores: () => reload("manual"),
  };
}
